import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence, MotionConfig } from 'framer-motion'
import { Trophy, Sparkles, X } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

/**
 * A one-shot, full-screen reward sequence played after a package purchase.
 * Runs five distinct animated acts back-to-back, then calls onComplete:
 *   0 confetti rain → 1 fireworks → 2 emoji pop → 3 trophy + message → 4 sparkle finale.
 * A Skip button (and Esc) jumps straight to onComplete.
 */

const COLORS = ['#ff4d8d', '#ff7a59', '#ffb84d', '#8b5cf6', '#38bdf8', '#2dd4bf', '#a3e635']
const rand = (min: number, max: number) => min + Math.random() * (max - min)
const pick = () => COLORS[Math.floor(rand(0, COLORS.length))]

// Per-act durations in ms; their sum is the total runtime (~7.3s).
const DURATIONS = [1300, 1600, 1300, 1800, 1500]

const ConfettiRain = () => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 90 }, (_, id) => ({
        id,
        left: rand(0, 100),
        delay: rand(0, 0.6),
        dur: rand(1.6, 2.9),
        color: pick(),
        size: rand(6, 13),
        rot: rand(0, 360),
        drift: rand(-50, 50),
      })),
    [],
  )
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: '-12vh', x: 0, opacity: 0, rotate: p.rot }}
          animate={{ y: '112vh', x: p.drift, opacity: [0, 1, 1, 0.9], rotate: p.rot + 540 }}
          transition={{ duration: p.dur, delay: p.delay, ease: 'easeIn' }}
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.42,
            background: p.color,
            borderRadius: 2,
          }}
        />
      ))}
    </div>
  )
}

const Fireworks = () => {
  const bursts = useMemo(
    () =>
      Array.from({ length: 6 }, (_, id) => ({
        id,
        cx: rand(15, 85),
        cy: rand(18, 62),
        delay: rand(0, 0.9),
        color: pick(),
        particles: Array.from({ length: 26 }, (_, i) => ({
          angle: (i / 26) * Math.PI * 2,
          dist: rand(60, 130),
        })),
      })),
    [],
  )
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bursts.map((burst) => (
        <div
          key={burst.id}
          style={{ position: 'absolute', left: `${burst.cx}%`, top: `${burst.cy}%` }}
        >
          {burst.particles.map((pt, i) => (
            <motion.span
              key={i}
              initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
              animate={{
                x: Math.cos(pt.angle) * pt.dist,
                y: Math.sin(pt.angle) * pt.dist,
                opacity: 0,
                scale: 0.4,
              }}
              transition={{ duration: 1.1, delay: burst.delay, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                width: 7,
                height: 7,
                borderRadius: 9999,
                background: burst.color,
                boxShadow: `0 0 8px ${burst.color}`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

const EmojiPop = () => {
  const emojis = ['🎉', '🎁', '✨', '🥳', '🎊']
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-3 sm:gap-6">
      {emojis.map((e, i) => (
        <motion.span
          key={i}
          className="text-5xl sm:text-7xl"
          initial={{ scale: 0, y: 50, opacity: 0 }}
          animate={{ scale: [0, 1.35, 1], y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: i * 0.12, ease: 'backOut' }}
        >
          {e}
        </motion.span>
      ))}
    </div>
  )
}

const SparkleFinale = () => {
  const stars = useMemo(
    () =>
      Array.from({ length: 28 }, (_, id) => ({
        id,
        left: rand(5, 95),
        top: rand(8, 92),
        delay: rand(0, 0.8),
        size: rand(10, 26),
      })),
    [],
  )
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {stars.map((s) => (
        <motion.div
          key={s.id}
          initial={{ scale: 0, opacity: 0, rotate: 0 }}
          animate={{ scale: [0, 1, 0.6], opacity: [0, 1, 0], rotate: 90 }}
          transition={{ duration: 1.2, delay: s.delay, ease: 'easeOut' }}
          style={{ position: 'absolute', left: `${s.left}%`, top: `${s.top}%` }}
        >
          <Sparkles style={{ width: s.size, height: s.size }} className="text-[#ffb84d]" />
        </motion.div>
      ))}
    </div>
  )
}

type CelebrationOverlayProps = {
  onComplete: () => void
}

const CelebrationOverlay = ({ onComplete }: CelebrationOverlayProps) => {
  const { language } = useLanguage()
  const [stage, setStage] = useState(0)

  // Advance through the acts; finish after the last one.
  useEffect(() => {
    if (stage >= DURATIONS.length) {
      onComplete()
      return
    }
    const id = setTimeout(() => setStage((s) => s + 1), DURATIONS[stage])
    return () => clearTimeout(id)
  }, [stage, onComplete])

  // Esc skips the whole sequence.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onComplete()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onComplete])

  return (
    <MotionConfig reducedMotion="user">
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#1e1438]/95 via-[#130b26]/95 to-black/95 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-label={language === 'ka' ? 'აღნიშვნა' : 'Celebration'}
      data-testid="celebration-overlay"
    >
      {/* Acts */}
      {(stage === 0 || stage === 4) && <ConfettiRain />}
      {stage === 1 && <Fireworks />}
      {stage === 2 && <EmojiPop />}
      {stage === 4 && <SparkleFinale />}

      {/* Trophy + message persist from act 3 to the finale */}
      <AnimatePresence>
        {stage >= 3 && (
          <motion.div
            className="relative z-10 flex flex-col items-center px-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0, rotate: -25, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 13 }}
              className="flex h-28 w-28 items-center justify-center rounded-bubble bg-grad-hot shadow-2xl shadow-[#ff4d8d]/40"
            >
              <Trophy className="h-14 w-14 text-white" />
            </motion.div>
            <motion.h2
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="mt-6 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl"
            >
              {language === 'ka' ? 'პაკეტი გააქტიურდა!' : 'Package activated!'}
            </motion.h2>
            <motion.p
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-2 max-w-xs text-base text-white/75"
            >
              {language === 'ka'
                ? 'შენი ღონისძიება მზადაა — მოდი, შევქმნათ დაუვიწყარი დღე!'
                : "Your event is ready — let's make it unforgettable!"}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skip */}
      <button
        type="button"
        onClick={onComplete}
        aria-label={language === 'ka' ? 'გამოტოვება' : 'Skip'}
        data-testid="celebration-skip"
        className="absolute right-4 top-4 z-20 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium text-white/80 backdrop-blur transition-colors hover:bg-white/20 hover:text-white"
      >
        {language === 'ka' ? 'გამოტოვება' : 'Skip'}
        <X className="h-4 w-4" />
      </button>
    </motion.div>
    </MotionConfig>
  )
}

export default CelebrationOverlay
